import { classNames, IGridAppearances, IGridColumnProperties, IGridProperties } from './Settings';
import { ColumnCollection } from './ColumnCollection';
import { GridColumn } from './GridColumn';
import { GridRow } from './GridRow';
import { enumerable } from '@Common/Core/CoreDecorator';

export class GridRender {
    private _gridProp: IGridProperties;
    public readonly root: JQuery;
    public headerTable: JQuery;
    public bodyTable: JQuery;
    public bodyTBody: JQuery;
    public bodyScrollableArea: JQuery;
    public headerScrollableArea: JQuery;
    public bodyContainer: JQuery;
    public readonly footer: JQuery;
    public headerContainer: JQuery;

    constructor(gridProp: IGridProperties) {
        this._gridProp = gridProp;
        this.root = GridRender.createRoot(gridProp.width, gridProp.height);
        if (gridProp.className) {
            this.root.addClass(gridProp.className);
        }
        if (gridProp.css) {
            this.root.css(gridProp.css);
        }
        this.createHeader();
        this.createBody();

        this.footer = this.createFooter();
        this.renderFirstBodyColumns();
    }

    public render() {
        this.headerContainer.appendTo(this.root);
        this.bodyContainer.appendTo(this.root);
        if (this._gridProp.footer.flexible) {
            this.footer.appendTo(this.bodyScrollableArea);
        } else {
            this.footer.appendTo(this.root);
        }
        $('<div>', { class: 'simpleGrid-DisableShade' }).appendTo(this.root);
    }

    public renderColumns(columns: ColumnCollection): GridColumn[] {
        const tableRow = $('<tr>', {
            class: classNames.row + ' simpleGrid-headerRow'
        }).appendTo(this.headerTable.children('thead'));

        return this._gridProp.columns.map((column: IGridColumnProperties, index: number) => {
            const tableColumn = $('<th>', {
                class: classNames.column,
                html: column.header || $('<span>', { text: column.title, class: 'simpleGrid-caption' })
            });

            if (this.appearances.headerCellStyleName) {
                tableColumn.addClass(this.appearances.headerCellStyleName);
            }

            tableRow.append(tableColumn);

            const bodyHeaderRow = $('.simpleGrid-body-firstRow th', this.bodyTable).eq(index);

            return new GridColumn(tableColumn, bodyHeaderRow, column, index, columns);
        });
    }

    public renderRow(): HTMLTableRowElement {
        const row = document.createElement('tr');
        row.className = classNames.row;

        this._gridProp.columns.forEach(() => {
            const cell = document.createElement('td');
            cell.className = classNames.cell;
            if (this.appearances.cellStyleName) {
                cell.className += ' ' + this.appearances.cellStyleName;
            }
            row.appendChild(cell);
        });
        return row;
    }

    private static createRoot(width: any, height: any): JQuery {
        return $('<div>', {
            class: 'simpleGrid-container',
            css: {
                width: width,
                height: height
            }
        });
    }

    @enumerable
    private get appearances(): IGridAppearances {
        return this._gridProp.appearances || ({} as any);
    }

    private createHeader(): void {
        this.headerContainer = GridRender.createScrollableTable(classNames.headerContainer, classNames.headerTable);
        this.headerTable = this.headerContainer.find('.' + classNames.headerTable);
        this.headerScrollableArea = this.headerContainer.find(`.${classNames.scrollableArea}`);
    }

    private static createScrollableTable(containerClass: string, tableClass: string): JQuery {
        return $(
            '' +
                `<div class="${containerClass}">
    <div class="simpleGrid-tableWrapper">
        <div class="${classNames.scrollableArea}">
            <table class="${tableClass}" cellpadding="0" cellspacing="0" border="0">
                <thead></thead>
                <tbody></tbody>
            </table>
        </div>
    </div>
</div>`
        );
    }

    private createBody() {
        this.bodyContainer = GridRender.createScrollableTable(classNames.bodyContainer, classNames.bodyTable);
        this.bodyScrollableArea = this.bodyContainer.find(`.${classNames.scrollableArea}`);
        this.bodyTable = this.bodyContainer.find(`.${classNames.bodyTable}`);
        this.bodyTBody = this.bodyTable.find('tbody');
    }

    private createFooter(): JQuery {
        const footer = $('<div>', { class: classNames.footer, css: this._gridProp.footer.css });
        const footerDef = this._gridProp.footer;
        if (footerDef) {
            if (footerDef.text) {
                footer.text(footerDef.text);
            } else if (footerDef.content) {
                footer.empty().append(footerDef.content);
            }
        }
        return footer;
    }

    public bodyIsFilled(): boolean {
        return this.bodyContainer.height() < this.bodyScrollableArea.height();
    }

    public static bodyFirstRowClasses = [classNames.row, 'simpleGrid-headerRow', 'simpleGrid-body-firstRow'].join(' ');

    public renderFirstBodyColumns(): JQuery[] {
        const tableRow = $('<tr>', {
            class: GridRender.bodyFirstRowClasses
        }).appendTo(this.bodyTable.children('thead'));

        return _(this._gridProp.columns)
            .map(() => {
                return $('<th>').addClass(classNames.column).appendTo(tableRow);
            })
            .value();
    }

    public isVisible(): boolean {
        return this.root.is(':visible');
    }

    public attachRow(gridRow: GridRow) {
        gridRow.tableRow().appendTo(this.bodyTBody);
    }

    public detachRows() {
        this.bodyTBody.children('tr').detach();
    }

    public destroy() {
        this.root.remove();
    }

    public isRenderedRow(row: GridRow): boolean {
        return row.tableRow().parent().length === 1;
    }
}
